import { baseApi } from '@app/store/index.api';

import {
  ICopyPolicyQuery,
  ICreatePolicyQuery,
  ICreateRuleQuery,
  IDeletePolicyQuery,
  IDeleteRuleQuery,
  IPolicyResponse,
  IPolicySettingsResponse,
  IReorderPoliciesQuery,
  IReorderResponse,
  IReorderRulesQuery,
  IRuleResponse,
  IUpdatePolicyQuery,
  IUpdateRuleQuery,
} from '../model/types';

const policyApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getPolicies: builder.query<IPolicyResponse, string | void>({
      query: query => ({
        url: query ? `/router/policy?${query}` : '/router/policy',
      }),
      providesTags: ['Policies'],
    }),
    createPolicy: builder.mutation<IPolicyResponse, ICreatePolicyQuery>({
      query: body => ({
        url: '/router/policy',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Policies'],
    }),
    updatePolicy: builder.mutation<IPolicyResponse, IUpdatePolicyQuery>({
      query: ({ id, ...body }) => ({
        url: `/router/policy/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Policies'],
    }),
    deletePolicy: builder.mutation<{ id: string }, IDeletePolicyQuery>({
      query: ({ id }) => ({
        url: `/router/policy/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Policies'],
    }),
    copyPolicy: builder.mutation<IPolicyResponse, ICopyPolicyQuery>({
      query: ({ id }) => ({
        url: `/router/policy/${id}/copy`,
        method: 'POST',
      }),
      invalidatesTags: ['Policies'],
    }),
    reorderPolicies: builder.mutation<IReorderResponse, IReorderPoliciesQuery>({
      query: body => ({
        url: '/router/policy/reorder',
        method: 'PUT',
        body,
      }),
      // The hook refetches the list explicitly so the optimistic order resets once.
      invalidatesTags: [],
    }),

    getRules: builder.query<IRuleResponse, string | void>({
      query: query => ({
        url: query ? `/router/rule?${query}` : '/router/rule',
      }),
      providesTags: ['PolicyRules'],
    }),
    createRule: builder.mutation<IRuleResponse, ICreateRuleQuery>({
      query: body => ({
        url: '/router/rule',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PolicyRules'],
    }),
    updateRule: builder.mutation<IRuleResponse, IUpdateRuleQuery>({
      query: ({ id, ...body }) => ({
        url: `/router/rule/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PolicyRules'],
    }),
    reorderRules: builder.mutation<IReorderResponse, IReorderRulesQuery>({
      query: ({ policy, ...body }) => ({
        url: `/router/policy/${policy}/rule/reorder`,
        method: 'PUT',
        body,
      }),
      // Success: refetch the rules list via the tag. Failure: invalidatesTags does
      // not fire on a rejected mutation, so force the reload here — the optimistic
      // order is no longer trusted and the list must re-sync to the server's set.
      invalidatesTags: ['PolicyRules'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(baseApi.util.invalidateTags(['PolicyRules']));
        }
      },
    }),
    deleteRule: builder.mutation<{ id: string }, IDeleteRuleQuery>({
      query: ({ id }) => ({
        url: `/router/rule/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PolicyRules'],
    }),

    getPolicySettings: builder.query<IPolicySettingsResponse, void>({
      query: () => ({
        url: '/router/settings',
      }),
      providesTags: ['PolicySettings'],
    }),
  }),
});

export const {
  useGetPoliciesQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useDeletePolicyMutation,
  useCopyPolicyMutation,
  useReorderPoliciesMutation,
  useGetRulesQuery,
  useCreateRuleMutation,
  useUpdateRuleMutation,
  useReorderRulesMutation,
  useDeleteRuleMutation,
  useGetPolicySettingsQuery,
} = policyApi;
